<template>
  <v-app dark class="tech-app-wrapper">
    <div class="circuit-overlay"></div>
    <div class="scanlines"></div>

    <section class="content-relative">
      <v-container class="pa-4" style="max-width: 1400px;">
        
        <v-row class="text-center mb-12">
          <v-col cols="12">
            <v-icon size="80" color="cyan accent-3" class="mb-4 glow-icon">mdi-shield-account-outline</v-icon>
            <h1 class="text-h3 font-weight-black white--text text-uppercase neon-text tracking-widest mb-2">
              Protocolos de Registro
            </h1>
            <div class="subtitle-text cyan--text text--lighten-3 mono-font">
              > Verificando requisitos de acceso al sistema .NI..._
            </div>
          </v-col>
        </v-row>

        <v-row class="mb-16">
          <v-col cols="12" md="10" lg="10" class="mx-auto">
            <v-card class="tech-card rounded-lg overflow-hidden elevation-10">
              <div class="card-header-neon pa-6 border-bottom-neon d-flex align-center">
                <v-icon left size="30" color="cyan accent-3">mdi-console-line</v-icon>
                <span class="text-h5 font-weight-bold white--text mono-font">>> REQUISITOS_DEL_SISTEMA</span>
              </div>
              
              <v-card-text class="pa-8">
                <v-row>
                  <v-col 
                    v-for="(requisito, index) in requisitos" 
                    :key="index"
                    cols="12" 
                    md="6" 
                    class="mb-4"
                  >
                    <v-hover v-slot="{ hover }">
                      <v-card 
                        class="requisito-node pa-4 d-flex align-center" 
                        :class="{'on-hover': hover}"
                        outlined
                      >
                        <div class="icon-container mr-5">
                          <v-avatar size="60" class="neon-border-circle" :class="hover ? 'spin-animation' : ''">
                            <v-icon size="30" :color="getNeonColor(index)">{{ requisito.icon }}</v-icon>
                          </v-avatar>
                        </div>
                        <div class="requisito-content">
                          <h4 class="text-h6 font-weight-bold white--text mb-1 text-uppercase">{{ requisito.title }}</h4>
                          <p class="grey--text text--lighten-1 mb-0 text-caption mono-font">{{ requisito.description }}</p>
                        </div>
                        <v-icon class="status-check" small color="green accent-3">mdi-check-bold</v-icon>
                      </v-card>
                    </v-hover>
                  </v-col>
                </v-row>
              </v-card-text>
              
              <v-divider class="neon-divider"></v-divider>
              
              <v-card-actions class="pa-6 justify-center bg-black-transparent">
                <v-btn 
                  outlined
                  color="cyan accent-3" 
                  class="neon-btn mx-2 glow-on-hover" 
                  large 
                  @click="scrollToDominios"
                >
                  <v-icon left>mdi-sitemap</v-icon>
                  EXPLORAR NODOS (.NI)
                </v-btn>
                <v-btn 
                  class="neon-bg black--text font-weight-bold mx-2" 
                  large 
                  href="/PasosDom"
                >
                  <v-icon left color="black">mdi-play-network</v-icon>
                  INICIAR SECUENCIA
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mb-12" id="tipos-dominio">
          <v-col cols="12">
            <div class="text-center mb-10">
              <h2 class="text-h4 font-weight-bold white--text mb-2 text-shadow-blue">
                <v-icon large color="blue accent-3" class="mr-3">mdi-server-network</v-icon>
                MATRIZ DE DOMINIOS
              </h2>
              <p class="grey--text mono-font">
                [SELECCIONE EL IDENTIFICADOR DE RED ADECUADO]
              </p>
            </div>

            <v-row>
              <v-col 
                v-for="(dominio, index) in dominiosNi" 
                :key="index"
                cols="12" 
                sm="6" 
                md="4" 
                lg="3" 
              >
                <v-hover v-slot="{ hover }">
                  <v-card 
                    class="dominio-tech-card text-center pa-6 fill-height d-flex flex-column" 
                    :class="{'hologram-active': hover}"
                    elevation="10"
                  >
                    <div class="tech-corner top-left"></div>
                    <div class="tech-corner top-right"></div>
                    <div class="tech-corner bottom-left"></div>
                    <div class="tech-corner bottom-right"></div>

                    <div class="icon-wrapper mb-6">
                      <div class="hex-outer" :style="`border-color: ${getNeonColorHex(index)}`">
                        <div class="hex-inner">
                          <v-icon size="40" color="white">{{ dominio.icon }}</v-icon>
                        </div>
                      </div>
                    </div>
                    
                    <h3 class="text-h6 font-weight-bold white--text mb-2 text-uppercase">{{ dominio.sector }}</h3>
                    
                    <div class="mb-4">
                      <v-chip 
                        class="neon-chip font-weight-bold" 
                        outlined
                        :color="getNeonColorHex(index)"
                      >
                        {{ dominio.dominio }}
                      </v-chip>
                    </div>
                    
                    <p class="grey--text text--lighten-1 body-2 mb-4 flex-grow-1">{{ dominio.descripcion }}</p>
                    
                    <div class="mt-auto">
                      <v-btn 
                        small 
                        text 
                        color="white"
                        class="hover-glow"
                        @click="showDominioInfo(dominio)"
                      >
                        <v-icon left small color="cyan">mdi-console</v-icon>
                        LEER DATA
                      </v-btn>
                    </div>
                  </v-card>
                </v-hover>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row class="mb-12">
          <v-col cols="12">
            <v-card class="tech-card border-blue" elevation="0">
              <v-card-title class="pa-6 border-bottom-neon">
                <v-icon left color="blue accent-3">mdi-chart-timeline-variant</v-icon>
                <span class="text-h6 white--text mono-font">MÉTRICAS DEL SISTEMA</span>
              </v-card-title>
              
              <v-card-text class="pa-0">
                <v-row no-gutters>
                  <v-col cols="12" md="4" class="pa-6 border-right-neon">
                    <div class="d-flex align-start">
                      <v-icon size="50" color="green accent-3" class="mr-4 mt-1 glow-icon">mdi-timer-sand</v-icon>
                      <div>
                        <h4 class="text-subtitle-1 font-weight-bold white--text mb-1">LATENCIA DE PROCESO</h4>
                        <div class="text-h4 font-weight-black green--text text--accent-3 mono-font mb-2">3-5 DÍAS</div>
                        <p class="caption grey--text mb-0">Tiempo estimado de validación 3-5 días.</p>
                      </div>
                    </div>
                  </v-col>
                  
                  <v-col cols="12" md="4" class="pa-6 border-right-neon">
                    <div class="d-flex align-start">
                      <v-icon size="50" color="blue accent-3" class="mr-4 mt-1 glow-icon">mdi-refresh-circle</v-icon>
                      <div>
                        <h4 class="text-subtitle-1 font-weight-bold white--text mb-1">CICLO DE VIDA</h4>
                        <div class="text-h4 font-weight-black blue--text text--accent-3 mono-font mb-2">365 DÍAS</div>
                        <p class="caption grey--text mb-0">Vigencia anual. Requiere renovación manual o automática.</p>
                      </div>
                    </div>
                  </v-col>
                  
                  <v-col cols="12" md="4" class="pa-6">
                    <div class="d-flex align-start">
                      <v-icon size="50" color="orange accent-3" class="mr-4 mt-1 glow-icon">mdi-face-agent</v-icon>
                      <div>
                        <h4 class="text-subtitle-1 font-weight-bold white--text mb-1">ENLACE DE SOPORTE</h4>
                        <div class="text-h4 font-weight-black orange--text text--accent-3 mono-font mb-2">100% ACTIVO</div>
                        <p class="caption grey--text mb-0">Personañ técnica y administrativamente capacitado.</p>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mb-6">
          <v-col cols="12">
            <v-card class="cta-tech-card pa-10 text-center relative overflow-hidden">
              <div class="bg-gradient-overlay"></div>
              <div class="content-z">
                <v-icon size="80" color="white" class="mb-6 glow-icon-large">mdi-rocket-launch-outline</v-icon>
                <h2 class="text-h3 font-weight-black white--text mb-4 text-shadow-blue">¿LISTO PARA EL DESPLIEGUE?</h2>
                <p class="text-h6 white--text mb-8 opacity-80 mono-font">
                  >> Inicie el protocolo de registro y asegure su identidad en la red.
                </p>
                
                <div>
                  <v-btn 
                    x-large 
                    class="neon-bg black--text font-weight-bold mr-4" 
                    href="/buscardominio"
                  >
                    <v-icon left color="black">mdi-magnify</v-icon>
                    ESCANEAR DOMINIO
                  </v-btn>
                  <v-btn 
                    x-large 
                    outlined
                    color="white" 
                    class="neon-btn" 
                    href="/contactenos"
                  >
                    <v-icon left>mdi-console</v-icon>
                    CONTACTAR ADMIN
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-dialog v-model="showDialog" max-width="500">
          <v-card class="tech-card border-neon">
            <v-card-title class="cyan darken-4 white--text border-bottom-neon">
              <v-icon left color="white">mdi-information-outline</v-icon>
              INFO_NODO: {{ selectedDominio ? selectedDominio.sector : '' }}
            </v-card-title>
            <v-card-text class="pa-6">
              <div class="text-center mb-6">
                <v-avatar size="80" class="neon-border-circle mb-3">
                  <v-icon size="40" color="cyan accent-3">{{ selectedDominio ? selectedDominio.icon : '' }}</v-icon>
                </v-avatar>
                <div>
                  <v-chip 
                    outlined
                    large 
                    color="cyan accent-3"
                    class="font-weight-bold"
                  >
                    {{ selectedDominio ? selectedDominio.dominio : '' }}
                  </v-chip>
                </div>
              </div>
              
              <div class="console-box pa-4 rounded mb-4">
                <p class="white--text body-1 mb-0 mono-font">{{ selectedDominio ? selectedDominio.descripcionCompleta : '' }}</p>
              </div>

              <div v-if="selectedDominio && selectedDominio.requisitosEspeciales">
                <h4 class="text-subtitle-1 font-weight-bold cyan--text mb-2 mono-font">>> REQUISITOS ESPECÍFICOS:</h4>
                <v-list dense color="transparent" dark>
                  <v-list-item v-for="(req, reqIndex) in selectedDominio.requisitosEspeciales" :key="reqIndex">
                    <v-list-item-icon>
                      <v-icon color="green accent-3" small>mdi-chevron-right</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="grey--text text--lighten-2">{{ req }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </v-card-text>
            <v-card-actions class="grey darken-4">
              <v-spacer></v-spacer>
              <v-btn color="red accent-2" text @click="showDialog = false">
                [X] CERRAR TERMINAL
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </v-container>
    </section>
  </v-app>
</template>

<script>
export default {
  name: "Requisitos",
  data() {
    return {
      showDialog: false,
      selectedDominio: null,
      requisitos: [
        { 
          title: "Mayoría de Edad", 
          description: "Acceso denegado a menores de 18 años. Requiere ID válida.",
          icon: "mdi-account-check" 
        },
        { 
          title: "Disponibilidad", 
          description: "El nodo debe estar libre en la red global.",
          icon: "mdi-web-check" 
        },
        { 
          title: "Cuenta de Usuario", 
          description: "Credenciales activas en la base de datos NIC.NI.",
          icon: "mdi-account-key" 
        },
        { 
          title: "Correo Electrónico", 
          description: "Canal de comunicación principal activo.",
          icon: "mdi-email-alert" 
        },
        { 
          title: "Documentación", 
          description: "Archivos digitales acreditados cargados al sistema.",
          icon: "mdi-file-certificate" 
        },
        { 
          title: "Pago Correspondiente", 
          description: "Transacción monetaria verificada.",
          icon: "mdi-credit-card-wireless" 
        },
      ],
      dominiosNi: [
        {
          sector: "Sector General",
          dominio: ".ni",
          icon: "mdi-earth",
          descripcion: "Para uso general, empresas e instituciones.",
          descripcionCompleta: "Dominio de primer nivel para uso general en Nicaragua. Ideal para empresas, organizaciones y proyectos que deseen una identidad nacional.",
          requisitosEspeciales: ["Cédula de Identidad o RUC", "Formulario de registro completo"]
        },
        {
          sector: "Comercial",
          dominio: ".com.ni",
          icon: "mdi-store",
          descripcion: "Para empresas y negocios comerciales.",
          descripcionCompleta: "Especialmente diseñado para empresas comerciales, tiendas en línea y negocios que operan en Nicaragua.",
          requisitosEspeciales: ["RUC vigente", "Comprobante de actividad comercial"]
        },
        {
          sector: "Educativo",
          dominio: ".edu.ni",
          icon: "mdi-school",
          descripcion: "Instituciones educativas y académicas.",
          descripcionCompleta: "Exclusivo para instituciones educativas, universidades, colegios y centros de formación en Nicaragua.",
          requisitosEspeciales: ["Constancia del MINED/INATEC/CNU", "Resolución de autorización"]
        },
        {
          sector: "Corporativo",
          dominio: ".co.ni",
          icon: "mdi-office-building",
          descripcion: "Corporaciones y grandes empresas.",
          descripcionCompleta: "Para corporaciones, holdings y grandes empresas con operaciones en el país.",
          requisitosEspeciales: ["RUC corporativo", "Estatutos registrados"]
        },
        {
          sector: "Tecnología",
          dominio: ".net.ni",
          icon: "mdi-lan",
          descripcion: "Proveedores de internet y tecnología.",
          descripcionCompleta: "Destinado a empresas de tecnología, proveedores de servicios de internet y empresas de telecomunicaciones.",
          requisitosEspeciales: ["Autorización TELCOR", "Licencia de operación"]
        },
        {
          sector: "Internacionales",
          dominio: ".int.ni",
          icon: "mdi-flag",
          descripcion: "Organizaciones internacionales.",
          descripcionCompleta: "Para organizaciones internacionales, embajadas y organismos con presencia en Nicaragua.",
          requisitosEspeciales: ["Acreditación diplomática", "Documentación matriz"]
        },
        {
          sector: "Gobierno",
          dominio: ".gob.ni",
          icon: "mdi-bank",
          descripcion: "Instituciones gubernamentales.",
          descripcionCompleta: "Exclusivo para instituciones del gobierno, ministerios y entidades públicas nicaragüenses.",
          requisitosEspeciales: ["Resolución ministerial", "Documentación oficial"]
        },
        {
          sector: "Organizaciones",
          dominio: ".org.ni",
          icon: "mdi-account-group",
          descripcion: "Organizaciones sin fines de lucro.",
          descripcionCompleta: "Para ONGs, fundaciones y asociaciones civiles registradas.",
          requisitosEspeciales: ["Resolución administrativa", "Número perpetuo OSFL"]
        },
        {
          sector: "Medios",
          dominio: ".tv.ni",
          icon: "mdi-television",
          descripcion: "Instituciones de medios.",
          descripcionCompleta: "Destinado a instituciones de medios de comunicación, televisión y radio.",
          requisitosEspeciales: ["Licencia de medios", "Registro en TELCOR"]
        },
      ]
    };
  },
  methods: {
    scrollToDominios() {
      const element = document.getElementById('tipos-dominio');
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    },
    showDominioInfo(dominio) {
      this.selectedDominio = dominio;
      this.showDialog = true;
    },
    // Colores Neon Rotativos
    getNeonColor(index) {
      const colors = ['cyan accent-3', 'blue accent-3', 'green accent-3', 'purple accent-3', 'pink accent-3', 'teal accent-3'];
      return colors[index % colors.length];
    },
    getNeonColorHex(index) {
      const colors = ['#00E5FF', '#2979FF', '#00E676', '#E040FB', '#F50057', '#1DE9B6'];
      return colors[index % colors.length];
    }
  }
};
</script>

<style scoped>
/* --- ESTILOS CYBERPUNK / HIGH-TECH COMPARTIDOS --- */

@import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;600&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap');

.tech-app-wrapper {
  background-color: #050a14 !important;
  font-family: 'Montserrat', sans-serif;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
}

.mono-font { font-family: 'Fira Code', monospace !important; }
.tracking-widest { letter-spacing: 2px; }

/* Efectos de Fondo */
.circuit-overlay {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background-image: 
    radial-gradient(circle at 10% 20%, rgba(0, 255, 255, 0.05) 0%, transparent 40%),
    linear-gradient(0deg, rgba(0,0,0,0.2) 50%, transparent 50%);
  background-size: cover, 4px 4px;
  z-index: 0;
}

.scanlines {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0) 50%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.2));
  background-size: 100% 4px;
  pointer-events: none;
  z-index: 1;
}

.content-relative { position: relative; z-index: 2; }

/* Texto Neón */
.neon-text { text-shadow: 0 0 10px rgba(0, 229, 255, 0.6), 0 0 20px rgba(0, 229, 255, 0.3); }
.text-shadow-blue { text-shadow: 0 2px 10px rgba(41, 121, 255, 0.5); }
.glow-icon { filter: drop-shadow(0 0 8px cyan); }
.glow-icon-large { filter: drop-shadow(0 0 15px white); }

/* Componentes Tech */
.tech-card {
  background: rgba(13, 25, 45, 0.9) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 255, 255, 0.15) !important;
}

.card-header-neon { background: rgba(0, 229, 255, 0.1); }
.border-bottom-neon { border-bottom: 1px solid rgba(0, 255, 255, 0.3) !important; }
.border-right-neon { border-right: 1px solid rgba(0, 255, 255, 0.2); }
.neon-divider { border-color: rgba(0, 255, 255, 0.2) !important; }
.border-blue { border: 1px solid rgba(41, 121, 255, 0.5) !important; }
.bg-black-transparent { background-color: rgba(0,0,0,0.4); }

/* Items de Requisito */
.requisito-node {
  background: rgba(0, 0, 0, 0.4) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  transition: all 0.3s;
}
.requisito-node.on-hover {
  background: rgba(0, 229, 255, 0.05) !important;
  border-color: rgba(0, 229, 255, 0.5) !important;
  transform: translateX(5px);
}
.neon-border-circle { border: 2px solid rgba(255, 255, 255, 0.2); background: transparent; }
.status-check { position: absolute; top: 10px; right: 10px; opacity: 0.5; }
.requisito-node.on-hover .status-check { opacity: 1; filter: drop-shadow(0 0 5px #00E676); }

/* Tarjetas de Dominio */
.dominio-tech-card {
  background: rgba(10, 20, 35, 0.8) !important;
  border: 1px solid rgba(41, 121, 255, 0.2) !important;
  position: relative;
  transition: all 0.3s;
}
.hologram-active {
  border-color: rgba(0, 229, 255, 0.8) !important;
  box-shadow: 0 0 20px rgba(0, 229, 255, 0.15) !important;
  transform: translateY(-10px);
}

/* Decoración Hexagonal */
.hex-outer {
  width: 80px; height: 80px;
  border: 2px solid;
  border-radius: 50%; /* Simplified hex for CSS */
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 10px rgba(255,255,255,0.1);
}
.hex-inner { animation: pulse 3s infinite; }

/* Chips y Botones */
.neon-chip { border-width: 1px; box-shadow: 0 0 5px currentColor; background: transparent !important; }
.neon-btn { border-width: 1px; transition: all 0.3s; }
.glow-on-hover:hover { box-shadow: 0 0 15px cyan; background: rgba(0, 229, 255, 0.1); }
.neon-bg {
  background: linear-gradient(90deg, #00e5ff, #00b8d4) !important;
  box-shadow: 0 0 15px rgba(0, 229, 255, 0.4);
  transition: transform 0.2s;
}
.neon-bg:hover { transform: scale(1.05); }

/* CTA Card */
.cta-tech-card {
  background: radial-gradient(circle, #1a237e 0%, #000000 100%);
  border: 1px solid #2979FF;
  position: relative;
}
.bg-gradient-overlay {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48ZyBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTAgMzhoNDB2Mkgwdi0yem0wLTJoNDB2Mkgwdi0yeiIvPjwvZz48L2c+PC9zdmc+');
  opacity: 0.3;
}
.content-z { position: relative; z-index: 2; }

/* Esquinas Tecnológicas */
.tech-corner {
  position: absolute; width: 10px; height: 10px;
  border-color: rgba(0, 229, 255, 0.5); border-style: solid;
  transition: all 0.3s;
}
.top-left { top: 0; left: 0; border-width: 2px 0 0 2px; }
.top-right { top: 0; right: 0; border-width: 2px 2px 0 0; }
.bottom-left { bottom: 0; left: 0; border-width: 0 0 2px 2px; }
.bottom-right { bottom: 0; right: 0; border-width: 0 2px 2px 0; }
.hologram-active .tech-corner { width: 20px; height: 20px; border-color: #00E5FF; }

/* Animaciones */
@keyframes spin { 100% { transform: rotate(360deg); } }
@keyframes pulse { 0% { opacity: 0.7; transform: scale(0.95); } 50% { opacity: 1; transform: scale(1.05); } 100% { opacity: 0.7; transform: scale(0.95); } }
.spin-animation { animation: spin 4s linear infinite; border-color: cyan; }

/* Utilidades */
.hover-glow:hover { text-shadow: 0 0 8px white; }
.console-box { background: rgba(0,0,0,0.5); border: 1px solid rgba(255,255,255,0.1); }
.border-neon { border: 1px solid #00E5FF !important; }

/* Responsive */
@media (max-width: 960px) {
  .border-right-neon { border-right: none; border-bottom: 1px solid rgba(0, 255, 255, 0.2); }
}
</style>